// Company-structure tags — full CRUD. Mutations invalidate the `Tags` tag so
// the list refetches after any change.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export interface Tag {
  id: string;
  title: string;
}

interface ApiTag {
  _id: string;
  title: string;
}

export const tagsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTags: build.query<Tag[], void>({
      query: () => ({ ...ROUTES.tags.list }),
      transformResponse: (res: ApiTag[]) => (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: ['Tags'],
    }),
    createTag: build.mutation<unknown, { title: string }>({
      query: (body) => ({ ...ROUTES.tags.create, data: body }),
      invalidatesTags: ['Tags'],
    }),
    updateTag: build.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ ...ROUTES.tags.update(id), data: { title } }),
      invalidatesTags: ['Tags'],
    }),
    deleteTag: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.tags.remove(id) }),
      invalidatesTags: ['Tags'],
    }),
    // Project tags (separate family from the structure tags; own list tag).
    getProjectTags: build.query<Tag[], void>({
      query: () => ({ ...ROUTES.tags.projectList }),
      transformResponse: (res: ApiTag[]) => (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: [{ type: 'Tags', id: 'PROJECT_LIST' }],
    }),
    createProjectTag: build.mutation<{ _id?: string }, { title: string }>({
      query: (body) => ({ ...ROUTES.tags.createProject, data: body }),
      invalidatesTags: [{ type: 'Tags', id: 'PROJECT_LIST' }],
    }),
    updateProjectTag: build.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ ...ROUTES.tags.updateProject(id), data: { title } }),
      invalidatesTags: [{ type: 'Tags', id: 'PROJECT_LIST' }],
    }),
    deleteProjectTag: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.tags.removeProject(id) }),
      invalidatesTags: [{ type: 'Tags', id: 'PROJECT_LIST' }],
    }),
    getPeopleTags: build.query<Tag[], void>({
      query: () => ({ ...ROUTES.tags.peopleList }),
      transformResponse: (res: ApiTag[]) => (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: [{ type: 'Tags', id: 'PEOPLE_LIST' }],
    }),
    createPeopleTag: build.mutation<{ _id?: string }, { title: string }>({
      query: (body) => ({ ...ROUTES.tags.createPeople, data: body }),
      invalidatesTags: [{ type: 'Tags', id: 'PEOPLE_LIST' }],
    }),
    updatePeopleTag: build.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ ...ROUTES.tags.updatePeople(id), data: { title } }),
      invalidatesTags: [{ type: 'Tags', id: 'PEOPLE_LIST' }],
    }),
    deletePeopleTag: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.tags.removePeople(id) }),
      invalidatesTags: [{ type: 'Tags', id: 'PEOPLE_LIST' }],
    }),
    // Workflow response tags (own list tag) — behind the workflow modal's
    // "Responses" button.
    getWorkflowResponseTags: build.query<Tag[], void>({
      query: () => ({ ...ROUTES.tags.workflowResponseList }),
      transformResponse: (res: ApiTag[]) => (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: [{ type: 'Tags', id: 'WORKFLOW_RESPONSE_LIST' }],
    }),
    createWorkflowResponseTag: build.mutation<{ _id?: string }, { title: string }>({
      query: (body) => ({ ...ROUTES.tags.createWorkflowResponse, data: body }),
      invalidatesTags: [{ type: 'Tags', id: 'WORKFLOW_RESPONSE_LIST' }],
    }),
    updateWorkflowResponseTag: build.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ ...ROUTES.tags.updateWorkflowResponse(id), data: { title } }),
      invalidatesTags: [{ type: 'Tags', id: 'WORKFLOW_RESPONSE_LIST' }],
    }),
    deleteWorkflowResponseTag: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.tags.removeWorkflowResponse(id) }),
      invalidatesTags: [{ type: 'Tags', id: 'WORKFLOW_RESPONSE_LIST' }],
    }),
    // Inspection tags (separate family; own list tag).
    getInspectionTags: build.query<Tag[], void>({
      query: () => ({ ...ROUTES.tags.inspectionList }),
      transformResponse: (res: ApiTag[]) => (res ?? []).map((t) => ({ id: t._id, title: t.title })),
      providesTags: [{ type: 'Tags', id: 'INSPECTION_LIST' }],
    }),
    createInspectionTag: build.mutation<{ _id?: string }, { title: string }>({
      query: (body) => ({ ...ROUTES.tags.createInspection, data: body }),
      invalidatesTags: [{ type: 'Tags', id: 'INSPECTION_LIST' }],
    }),
    updateInspectionTag: build.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ ...ROUTES.tags.updateInspection(id), data: { title } }),
      invalidatesTags: [{ type: 'Tags', id: 'INSPECTION_LIST' }],
    }),
    deleteInspectionTag: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.tags.removeInspection(id) }),
      invalidatesTags: [{ type: 'Tags', id: 'INSPECTION_LIST' }],
    }),
  }),
});

export const {
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetProjectTagsQuery,
  useCreateProjectTagMutation,
  useUpdateProjectTagMutation,
  useDeleteProjectTagMutation,
  useGetPeopleTagsQuery,
  useCreatePeopleTagMutation,
  useUpdatePeopleTagMutation,
  useDeletePeopleTagMutation,
  useGetWorkflowResponseTagsQuery,
  useCreateWorkflowResponseTagMutation,
  useUpdateWorkflowResponseTagMutation,
  useDeleteWorkflowResponseTagMutation,
  useGetInspectionTagsQuery,
  useCreateInspectionTagMutation,
  useUpdateInspectionTagMutation,
  useDeleteInspectionTagMutation,
} = tagsApi;
